import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { cx } from "../../../utils/cx";
import { Button, EmptyState, Loading, Textarea } from "../../ui";
import { errorMessageOf, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY } from "../model";
import { formatDateTime } from "../review/model";

const MAX_MESSAGE = 2000;

export const messagesKey = (path) => [...SCHOLARSHIPS_KEY, "messages", path];

const BRD_LIST_KEY = [...SCHOLARSHIPS_KEY, "brd"];

/**
 * The thread between a student and BRD on one government loan. `path` is the thread's endpoint
 * (student/ or staff/); `side` is who is reading (STUDENT or BRD), or null for HEC, who only read.
 * `client` is the API the loan lives on and `toMessage` reads its messages as { id, body, sentAt,
 * author: { side, name } }; `listKey` is BRD's list, which shows who waits for an answer.
 */
export default function MessageThread({ path, side, client = scholarshipsApi, toMessage = (m) => m, listKey = BRD_LIST_KEY }) {
  const queryClient = useQueryClient();
  const { data: messages = [], isPending, isError, refetch } = useQuery({ queryKey: messagesKey(path), queryFn: async () => (await client.get(path)).data.map(toMessage) });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await client.post(path, { body: draft.trim() });
      setDraft("");
      queryClient.invalidateQueries({ queryKey: messagesKey(path) });
      queryClient.invalidateQueries({ queryKey: listKey });
    } catch (error) {
      console.error(`Failed to send a message to ${path}:`, error);
      toast.error(errorMessageOf(error, "Failed to send the message. Please try again."));
    } finally {
      setSending(false);
    }
  };

  if (isPending) return <Loading label="Loading the messages…" />;
  if (isError) return <EmptyState title="Could not load the messages" onRefresh={refetch} />;

  return (
    <div className="flex flex-col gap-4">
      {messages.length === 0 ? (
        <p className="text-sm text-slate-500">No messages yet.</p>
      ) : (
        <ol className="flex max-h-96 flex-col gap-3 overflow-y-auto">
          {messages.map((m) => {
            const mine = m.author.side === side;
            return (
              <li key={m.id} className={cx("flex max-w-[85%] flex-col gap-1 rounded-lg px-3 py-2 text-sm", mine ? "self-end bg-brand/10" : "self-start bg-slate-100")}>
                <span className="text-xs text-slate-500">
                  <span className="font-medium text-slate-700">{m.author.name}</span> · {formatDateTime(m.sentAt)}
                </span>
                <p className="whitespace-pre-wrap text-slate-800">{m.body}</p>
              </li>
            );
          })}
        </ol>
      )}
      {side && (
        <div className="flex flex-col gap-2">
          <Textarea label="Message" rows={3} maxLength={MAX_MESSAGE} value={draft} onChange={(e) => setDraft(e.target.value)} />
          <div className="flex justify-end">
            <Button size="sm" onClick={send} loading={sending} disabled={!draft.trim()} icon={<PaperAirplaneIcon className="h-4 w-4" />}>
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
